import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import BASE_URL from "../../../../config/config";
import "../../../../css/AddPost.css";

export default function AddPost() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    summary: "",
    content: "",
    category_id: "",
    tags: [],
    status: "draft",
  });
  const [coverImage, setCoverImage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [csrfToken, setCsrfToken] = useState("");
  const [newTagInput, setNewTagInput] = useState("");
  const [newCategoryInput, setNewCategoryInput] = useState("");
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
  
  // ✅ اضافه کردن state برای تعیین نوع ادیتور
  const [useSimpleEditor, setUseSimpleEditor] = useState(false);

  useEffect(() => {
    fetch(`${BASE_URL}/csrf-token`, {
      method: "GET",
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setCsrfToken(data.csrfToken))
      .catch(console.error);

    fetch(`${BASE_URL}/blog/categories`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setCategories(Array.isArray(data.rows) ? data.rows : []))
      .catch(() => setCategories([]));

    fetch(`${BASE_URL}/blog/tags`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setTagsList(data?.data || []))
      .catch(() => setTagsList([]));
  }, []);

  const generateSlug = (text) => {
    if (!text) return "";
    return text
      .replace(/[\s]+/g, "_")
      .replace(/[^\u0600-\u06FFa-zA-Z0-9_]/g, "")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData((prev) => ({
      ...prev,
      title,
      slug: generateSlug(title),
    }));
  };

  const handleSlugChange = (e) => {
    const slug = generateSlug(e.target.value);
    setFormData((prev) => ({ ...prev, slug }));
  };

  const handleEditorChange = (content) => {
    setFormData((prev) => ({ ...prev, content }));
  };

  const handleFileChange = (e) => {
    setCoverImage(e.target.files[0]);
  };

  const addNewTag = async () => {
    if (!newTagInput.trim()) return;

    try {
      setIsLoading(true);
      const res = await fetch(`${BASE_URL}/blog/tags`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          "CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify({
          name: newTagInput.trim(),
          slug: generateSlug(newTagInput.trim()),
        }),
      });

      const result = await res.json();

      if (res.ok) {
        const newTag = { id: result.id, name: newTagInput.trim() };
        setTagsList((prev) => [...prev, newTag]);
        setFormData((prev) => ({
          ...prev,
          tags: [...prev.tags, result.id.toString()],
        }));
        setNewTagInput("");
        setMessage("تگ با موفقیت اضافه شد");
      } else {
        setMessage(result.message || "خطا در ایجاد تگ جدید");
      }
    } catch (err) {
      setMessage("خطا در برقراری ارتباط با سرور");
    } finally {
      setIsLoading(false);
    }
  };

  const removeTag = (tagId) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((id) => id !== tagId.toString()),
    }));
  };

  const addNewCategory = async () => {
    if (!newCategoryInput.trim()) {
      setMessage("نام دسته‌بندی نمی‌تواند خالی باشد");
      return;
    }

    try {
      setIsAddingCategory(true);

      const res = await fetch(`${BASE_URL}/blog/categories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify({
          name: newCategoryInput.trim(),
          slug: generateSlug(newCategoryInput.trim()),
        }),
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.message || "خطا در ایجاد دسته‌بندی");
      }

      setCategories((prev) => [
        ...prev,
        { id: result.id, name: newCategoryInput.trim() },
      ]);
      setFormData((prev) => ({ ...prev, category_id: result.id.toString() }));
      setNewCategoryInput("");
      setShowAddCategoryForm(false);
      setMessage("دسته‌بندی با موفقیت اضافه شد");
    } catch (err) {
      setMessage(err.message || "خطا در ایجاد دسته‌بندی");
    } finally {
      setIsAddingCategory(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");

    const newErrors = {};
    if (!formData.title) newErrors.title = "عنوان الزامی است";
    if (!formData.slug) newErrors.slug = "Slug الزامی است";
    if (!formData.content) newErrors.content = "محتوا الزامی است";
    if (!formData.category_id) newErrors.category_id = "دسته‌بندی الزامی است";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsLoading(false);
      return;
    }

    const data = new FormData();
    data.append("title", formData.title);
    data.append("slug", formData.slug);
    data.append("summary", formData.summary);
    data.append("content", formData.content);
    data.append("category_id", formData.category_id);
    data.append("status", formData.status);

    formData.tags.forEach((tag) => data.append("tags[]", tag));

    if (coverImage) data.append("cover_image", coverImage);

    try {
      const res = await fetch(`${BASE_URL}/blog/posts`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: data,
      });

      const result = await res.json();

      if (!res.ok) {
        if (result.errors) setErrors(result.errors);
        setMessage(result.message || "خطا در ثبت پست");
        return;
      }

      setMessage("پست با موفقیت ثبت شد");
      setTimeout(() => navigate("/admin/blog/posts"), 1500);
    } catch (err) {
      setMessage("خطا در برقراری ارتباط با سرور");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="post-form-container">
      <h2>افزودن پست جدید</h2>

      {message && (
        <div
          className={`alert ${message.includes("موفق") ? "success" : "error"}`}
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="form-row">
          <div className="form-group">
            <label>
              عنوان پست <span className="required">*</span>
            </label>
            <input
              name="title"
              value={formData.title}
              onChange={handleTitleChange}
              className={errors.title ? "error" : ""}
            />
            {errors.title && <span className="error-text">{errors.title}</span>}
          </div>

          <div className="form-group">
            <label>
              Slug (لینک یکتا) <span className="required">*</span>
            </label>
            <input
              name="slug"
              value={formData.slug}
              onChange={handleSlugChange}
              className={errors.slug ? "error" : ""}
            />
            {errors.slug && <span className="error-text">{errors.slug}</span>}
          </div>
        </div>

        <div className="form-group">
          <label>خلاصه پست</label>
          <textarea
            name="summary"
            value={formData.summary}
            onChange={handleChange}
            rows="3"
          />
        </div>

        <div className="form-group">
          <label>
            محتوا <span className="required">*</span>
          </label>
          
          {/* ✅ دکمه تغییر نوع ادیتور */}
          <div style={{ marginBottom: "10px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={useSimpleEditor}
                onChange={(e) => setUseSimpleEditor(e.target.checked)}
              />
              <span>استفاده از ادیتور ساده (بدون نیاز به اینترنت)</span>
            </label>
          </div>

          {/* ✅ نمایش ادیتور بر اساس انتخاب */}
          {useSimpleEditor ? (
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              rows="15"
              style={{
                width: "100%",
                padding: "10px",
                fontFamily: "inherit",
                fontSize: "14px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                resize: "vertical"
              }}
              className={errors.content ? "error" : ""}
            />
          ) : (
            <Editor
              apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
              value={formData.content}
              init={{
                height: 500,
                menubar: true,
                plugins: ["fullscreen", "wordcount"],
                toolbar:
                  "undo redo | formatselect | bold italic backcolor | \
                         alignleft aligncenter alignright alignjustify | \
                         bullist numlist outdent indent | removeformat | help",
                directionality: "rtl",
                language: "fa",
              }}
              onEditorChange={handleEditorChange}
            />
          )}
          
          {errors.content && (
            <span className="error-text">{errors.content}</span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              دسته‌بندی <span className="required">*</span>
            </label>

            {!showAddCategoryForm ? (
              <div className="category-select-container">
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  className={errors.category_id ? "error" : ""}
                >
                  <option value="">انتخاب کنید</option>
                  {Array.isArray(categories) &&
                    categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowAddCategoryForm(true)}
                  className="add-category-btn"
                >
                  + افزودن دسته‌بندی جدید
                </button>
              </div>
            ) : (
              <div className="new-category-input">
                <input
                  type="text"
                  value={newCategoryInput}
                  onChange={(e) => setNewCategoryInput(e.target.value)}
                  placeholder="نام دسته‌بندی جدید..."
                  autoFocus
                />
                <div className="category-form-actions">
                  <button
                    type="button"
                    onClick={addNewCategory}
                    disabled={isAddingCategory}
                    className="confirm-add-category"
                  >
                    {isAddingCategory ? "در حال افزودن..." : "تایید"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddCategoryForm(false);
                      setNewCategoryInput("");
                    }}
                    className="cancel-add-category"
                  >
                    انصراف
                  </button>
                </div>
              </div>
            )}

            {errors.category_id && (
              <span className="error-text">{errors.category_id}</span>
            )}
          </div>

          <div className="form-group tags-group">
            <label>تگ‌ها</label>
            <div className="tags-management">
              <div className="tags-selection">
                <select
                  multiple
                  className="tags-select"
                  onChange={(e) => {
                    const selectedTags = Array.from(
                      e.target.selectedOptions,
                      (option) => option.value
                    );
                    setFormData((prev) => ({ ...prev, tags: selectedTags }));
                  }}
                  value={formData.tags}
                >
                  {tagsList.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="tags-actions">
                <Link to="/admin/blog/tags" className="manage-tags-btn">
                  مدیریت تگ‌ها
                </Link>
              </div>
            </div>

            <div className="new-tag-section">
              <div className="new-tag-input">
                <input
                  type="text"
                  value={newTagInput}
                  onChange={(e) => setNewTagInput(e.target.value)}
                  placeholder="نام تگ جدید..."
                  onKeyDown={(e) => e.key === "Enter" && addNewTag()}
                />
                <button
                  type="button"
                  onClick={addNewTag}
                  disabled={!newTagInput.trim() || isLoading}
                  className="add-tag-btn"
                >
                  افزودن
                </button>
              </div>
            </div>

            <div className="selected-tags">
              {formData.tags.map((tagId) => {
                const tag = tagsList.find(
                  (t) => t && t.id.toString() === tagId.toString()
                );
                return tag ? (
                  <span key={tag.id} className="tag">
                    {tag.name}
                    <button
                      type="button"
                      onClick={() => removeTag(tag.id)}
                      className="remove-tag"
                    >
                      ×
                    </button>
                  </span>
                ) : null;
              })}
            </div>
          </div>

          <div className="form-group">
            <label>وضعیت</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="draft">پیش‌نویس</option>
              <option value="published">منتشر شده</option>
              <option value="archived">آرشیو</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>تصویر کاور</label>
          <div className="file-upload">
            <input
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleFileChange}
              id="cover-upload"
            />
            <label htmlFor="cover-upload" className="upload-btn">
              {coverImage ? coverImage.name : "انتخاب تصویر"}
            </label>
            {coverImage && (
              <button
                type="button"
                onClick={() => setCoverImage(null)}
                className="remove-file"
              >
                حذف
              </button>
            )}
          </div>
          <small>فرمت‌های مجاز: JPG, PNG (حداکثر 5MB)</small>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={isLoading} className="btn-submit">
            {isLoading ? "در حال ذخیره..." : "ذخیره پست"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/blog/posts")}
            className="btn-cancel"
          >
            انصراف
          </button>
        </div>
      </form>
    </div>
  );
}